// Queue column rendering for the workbench panel.
//
// Extracted from workbench-ui.ts to stay within the 400-line module cap.
// Manages in-flight job rows with live countdown timers (1Hz via PixiJS ticker)
// and completed-unclaimed rows with a claim affordance.
//
// Phase 7 Block D — per-row claim + per-row cancel. Each row's button
// passes the row's jobId via `onClaimRequest` / `onCancelRequest`; the
// workshop tray no longer drains as one batch and queued/running jobs
// gained a Cancel affordance backed by the existing cancelCraft RPC
// (full refund for both queued and running, see player-workshop.ts).

import { Application, Container, Graphics, Text } from "pixi.js";
import { getRecipe } from "../../shared/recipes.ts";
import { parseItemId } from "../../shared/quality.ts";
import { getItemDef } from "../../shared/items.ts";
import type { WorkshopJob, WorkshopState } from "../../shared/workshop-messages.ts";

// -----------------------------------------------------------------------
// Style constants
// -----------------------------------------------------------------------

const PAD = 12; // contracts:allow-literal
const ROW_H = 36; // contracts:allow-literal
const ROW_GAP = 2; // contracts:allow-literal
const ROW_CORNER = 3; // contracts:allow-literal
const SECTION_H = 22; // contracts:allow-literal

const BG_QUEUED = 0x1a1a30;
const BG_RUNNING = 0x1a2a1a;
const BG_COMPLETED = 0x1a2a20;
const BG_HOVER_CLAIM = 0x2a4a30;
const BORDER_RUNNING = 0x33cc33;
const BORDER_CLAIM = 0x44cc88;
const SECTION_BG = 0x1a1a3a;
const BTN_CANCEL_BG = 0x6a3a2a;
const BTN_CANCEL_HOVER = 0x8a4a3a;
const BTN_CANCEL_BORDER = 0xaa5544;
const BTN_TEXT = 0xeeeeff;

const TEXT_ACTIVE = 0xeeeeff;
const TEXT_MUTED = 0x888899;
const TEXT_RUNNING = 0x88dd88;
const TEXT_CLAIM = 0x55ee88;
const TEXT_SECTION = 0x9999cc;
const TEXT_COMPLETED_NAME = 0xaaddbb;

const NAME_SIZE = 12; // contracts:allow-literal
const DETAIL_SIZE = 10; // contracts:allow-literal
const SECTION_SIZE = 11; // contracts:allow-literal
const CANCEL_BTN_W = 56; // contracts:allow-literal — fits "Cancel" + cushion px
const CANCEL_BTN_H = 22; // contracts:allow-literal — fits inside ROW_H (36) with vertical padding
const CLAIM_BTN_W = 60; // contracts:allow-literal — fits "[CLAIM]" inside the claim-row right edge
const BTN_CORNER = 3; // contracts:allow-literal — px corner radius
const BTN_LABEL_SIZE = 10; // contracts:allow-literal — button label font size

// -----------------------------------------------------------------------
// WorkbenchQueue
// -----------------------------------------------------------------------

export class WorkbenchQueue {
  private readonly app: Application;
  private col: Container | null = null;

  // jobId → { text node, completion timestamp }
  private countdownData = new Map<string, { text: Text; completesAt: number }>();
  private tickerFn: ((ticker: { deltaTime: number }) => void) | null = null;
  private lastTickMs = 0;

  // Per-row callbacks — wired by WorkbenchUI on every rebuild. Phase 7
  // Block D split the historical claim-all into per-row claim + a new
  // per-row cancel for queued/running jobs.
  onClaimRequest: ((jobId: string) => void) | null = null;
  onCancelRequest: ((jobId: string) => void) | null = null;

  constructor(app: Application) {
    this.app = app;
  }

  // Build the queue column into `parent` at the given position.
  build(parent: Container, x: number, y: number, width: number, height: number, workshop: WorkshopState): void {
    this.stopTicker();
    this.countdownData.clear();

    const col = new Container();
    col.x = x;
    col.y = y;
    parent.addChild(col);
    this.col = col;

    let cursor = PAD / 2; // contracts:allow-literal — small top pad
    cursor = this.buildSection(col, cursor, width, "In Progress", workshop.jobs, false);
    cursor += PAD;
    this.buildSection(col, cursor, width, "Ready to Claim", workshop.completedUnclaimed, true);

    void height; // reserved for future scroll

    this.startTicker();
  }

  // Replace queue contents with a fresh workshop snapshot. Called by WorkbenchUI.update().
  update(parent: Container, x: number, y: number, width: number, height: number, workshop: WorkshopState): void {
    if (this.col) {
      this.col.parent?.removeChild(this.col);
      this.col.destroy({ children: true });
      this.col = null;
    }
    this.build(parent, x, y, width, height, workshop);
  }

  // Called by WorkbenchUI.close() — stops the ticker and tears down refs.
  destroy(): void {
    this.stopTicker();
    this.countdownData.clear();
    this.col = null;
  }

  // -----------------------------------------------------------------------
  // Section rendering
  // -----------------------------------------------------------------------

  private buildSection(
    parent: Container,
    startY: number,
    width: number,
    label: string,
    jobs: WorkshopJob[],
    isClaim: boolean,
  ): number {
    let y = startY;

    const headerBg = new Graphics()
      .rect(PAD / 2, y, width - PAD, SECTION_H) // contracts:allow-literal
      .fill({ color: SECTION_BG });
    parent.addChild(headerBg);

    const headerText = new Text({
      text: label,
      style: { fontSize: SECTION_SIZE, fill: TEXT_SECTION, fontFamily: "monospace", fontWeight: "bold" },
    });
    headerText.x = PAD;
    headerText.y = y + Math.round((SECTION_H - headerText.height) / 2); // contracts:allow-literal
    parent.addChild(headerText);
    y += SECTION_H + ROW_GAP;

    if (jobs.length === 0) {
      const emptyText = new Text({
        text: isClaim ? "Nothing ready to claim" : "No jobs queued",
        style: { fontSize: DETAIL_SIZE, fill: TEXT_MUTED, fontFamily: "monospace", fontStyle: "italic" },
      });
      emptyText.x = PAD;
      emptyText.y = y + 6; // contracts:allow-literal — small indent for visual separation
      parent.addChild(emptyText);
      return y + ROW_H;
    }

    for (const job of jobs) {
      y = isClaim
        ? this.buildClaimRow(parent, y, width, job)
        : this.buildJobRow(parent, y, width, job);
      y += ROW_GAP;
    }
    return y;
  }

  // -----------------------------------------------------------------------
  // In-flight job row
  // -----------------------------------------------------------------------

  private buildJobRow(parent: Container, y: number, width: number, job: WorkshopJob): number {
    const rowW = width - PAD;
    const isRunning = job.status === "running";
    const bgColor = isRunning ? BG_RUNNING : BG_QUEUED;

    const container = new Container();
    container.x = PAD / 2; // contracts:allow-literal
    container.y = y;
    parent.addChild(container);

    const bg = new Graphics().roundRect(0, 0, rowW, ROW_H, ROW_CORNER).fill({ color: bgColor });
    if (isRunning) {
      bg.stroke({ color: BORDER_RUNNING, width: 1 }); // contracts:allow-literal
    }
    container.addChild(bg);

    const recipeName = getRecipeName(job.recipeId);
    const nameText = new Text({
      text: isRunning ? `▶ ${recipeName}` : `  ${recipeName}`,
      style: {
        fontSize: NAME_SIZE,
        fill: isRunning ? TEXT_RUNNING : TEXT_ACTIVE,
        fontFamily: "monospace",
        fontWeight: isRunning ? "bold" : "normal",
      },
    });
    nameText.x = PAD / 2; // contracts:allow-literal
    nameText.y = Math.round((ROW_H / 2 - nameText.height) / 2) + 2; // contracts:allow-literal
    container.addChild(nameText);

    const countdownText = new Text({
      text: formatCountdown(job.completesAt),
      style: { fontSize: DETAIL_SIZE, fill: TEXT_MUTED, fontFamily: "monospace" },
    });
    countdownText.x = PAD / 2; // contracts:allow-literal
    countdownText.y = nameText.y + nameText.height + 2; // contracts:allow-literal
    container.addChild(countdownText);
    this.countdownData.set(job.jobId, { text: countdownText, completesAt: job.completesAt });

    // Per-row Cancel button (Phase 7 Block D). Sits at the right edge,
    // replacing the prior plain-text status indicator. The status word
    // would visually fight the button at this row height, so it's
    // dropped here — the row background already encodes queued vs
    // running (BG_RUNNING + BORDER_RUNNING are the running visual).
    this.buildRowButton(
      container,
      rowW - CANCEL_BTN_W - PAD / 2, // contracts:allow-literal
      Math.round((ROW_H - CANCEL_BTN_H) / 2), // contracts:allow-literal
      CANCEL_BTN_W,
      CANCEL_BTN_H,
      "Cancel",
      { bg: BTN_CANCEL_BG, hover: BTN_CANCEL_HOVER, border: BTN_CANCEL_BORDER },
      () => this.onCancelRequest?.(job.jobId),
    );

    return y + ROW_H;
  }

  // -----------------------------------------------------------------------
  // Completed-unclaimed claim row
  // -----------------------------------------------------------------------

  private buildClaimRow(parent: Container, y: number, width: number, job: WorkshopJob): number {
    const rowW = width - PAD;

    const container = new Container();
    container.x = PAD / 2; // contracts:allow-literal
    container.y = y;
    parent.addChild(container);

    const bg = new Graphics();
    bg
      .roundRect(0, 0, rowW, ROW_H, ROW_CORNER)
      .fill({ color: BG_COMPLETED })
      .stroke({ color: BORDER_CLAIM, width: 1 }); // contracts:allow-literal
    container.addChild(bg);

    const recipeName = getRecipeName(job.recipeId);
    const nameText = new Text({
      text: `✓ ${recipeName}`,
      style: { fontSize: NAME_SIZE, fill: TEXT_COMPLETED_NAME, fontFamily: "monospace", fontWeight: "bold" },
    });
    nameText.x = PAD / 2; // contracts:allow-literal
    nameText.y = Math.round((ROW_H / 2 - nameText.height) / 2) + 2; // contracts:allow-literal
    container.addChild(nameText);

    const outputLabel = job.output ? formatOutput(job.output.itemId) : "unknown output";
    const outputText = new Text({
      text: outputLabel,
      style: { fontSize: DETAIL_SIZE, fill: TEXT_MUTED, fontFamily: "monospace" },
    });
    outputText.x = PAD / 2; // contracts:allow-literal
    outputText.y = nameText.y + nameText.height + 2; // contracts:allow-literal
    container.addChild(outputText);

    // Per-row CLAIM button (Phase 7 Block D). Each row claims only its
    // own job. The whole-row hover affordance was dropped because it
    // was misleading once claims became per-row — it suggested clicking
    // anywhere on the row would claim, but the action lives on the
    // button now to be precise about which job is being claimed.
    this.buildRowButton(
      container,
      rowW - CLAIM_BTN_W - PAD / 2, // contracts:allow-literal
      Math.round((ROW_H - CANCEL_BTN_H) / 2), // contracts:allow-literal
      CLAIM_BTN_W,
      CANCEL_BTN_H,
      "CLAIM",
      { bg: 0x2a6a3a, hover: 0x3a8a4a, border: 0x44aa55 }, // contracts:allow-literal — claim palette
      () => this.onClaimRequest?.(job.jobId),
    );

    // Keep the claim text class referenced so reviewers don't see it as
    // dead. The class drives the bold green job-name above.
    void TEXT_CLAIM;
    void BG_HOVER_CLAIM;

    return y + ROW_H;
  }

  // Shared button factory for the per-row Cancel/Claim affordances. Kept
  // local rather than reusing market-ui-widgets.makeRowButton because the
  // workbench palette is already defined here and a cross-module import
  // for two callsites isn't worth the coupling.
  private buildRowButton(
    parent: Container,
    x: number,
    y: number,
    w: number,
    h: number,
    label: string,
    palette: { bg: number; hover: number; border: number },
    onClick: () => void,
  ): void {
    const c = new Container();
    c.x = x;
    c.y = y;
    c.eventMode = "static";
    c.cursor = "pointer";
    const bg = new Graphics();
    const draw = (hover: boolean) => {
      bg.clear();
      bg
        .roundRect(0, 0, w, h, BTN_CORNER)
        .fill({ color: hover ? palette.hover : palette.bg })
        .stroke({ color: palette.border, width: 1 }); // contracts:allow-literal
    };
    draw(false);
    c.addChild(bg);
    const t = new Text({
      text: label,
      style: { fontSize: BTN_LABEL_SIZE, fill: BTN_TEXT, fontFamily: "monospace", fontWeight: "bold" },
    });
    t.x = Math.round((w - t.width) / 2); // contracts:allow-literal — centering idiom
    t.y = Math.round((h - t.height) / 2); // contracts:allow-literal — centering idiom
    c.addChild(t);
    c.on("pointerover", () => draw(true));
    c.on("pointerout", () => draw(false));
    c.on("pointertap", onClick);
    parent.addChild(c);
  }

  // -----------------------------------------------------------------------
  // Ticker — updates countdown texts at ~1Hz
  // -----------------------------------------------------------------------

  private startTicker(): void {
    if (this.tickerFn) return;
    this.lastTickMs = Date.now();
    const fn = () => {
      const now = Date.now();
      if (now - this.lastTickMs < 1000) return; // contracts:allow-literal — 1Hz cadence
      this.lastTickMs = now;
      for (const entry of this.countdownData.values()) {
        entry.text.text = formatCountdown(entry.completesAt);
      }
    };
    this.tickerFn = fn;
    this.app.ticker.add(fn);
  }

  private stopTicker(): void {
    if (this.tickerFn) {
      this.app.ticker.remove(this.tickerFn);
      this.tickerFn = null;
    }
  }
}

// -----------------------------------------------------------------------
// Pure helpers
// -----------------------------------------------------------------------

function getRecipeName(recipeId: string): string {
  return getRecipe(recipeId)?.name ?? recipeId;
}

function formatCountdown(completesAt: number): string {
  const remaining = completesAt - Date.now();
  if (remaining <= 0) return "completing...";
  const secs = Math.ceil(remaining / 1000); // contracts:allow-literal — ms to seconds
  if (secs < 60) return `${secs}s`; // contracts:allow-literal
  const m = Math.floor(secs / 60); // contracts:allow-literal
  const s = secs % 60; // contracts:allow-literal
  return `${m}m ${s}s`;
}

function formatOutput(itemId: string): string {
  const { baseId, quality } = parseItemId(itemId);
  const itemName = getItemDef(baseId)?.name ?? baseId;
  if (!quality) return itemName;
  const qualityLabel = quality.charAt(0).toUpperCase() + quality.slice(1);
  return `${qualityLabel} ${itemName}`;
}
